import React, { useEffect, useMemo, useState } from 'react'

import { FulfillmentType } from 'src/apollo/onlineOrdering'

import DropDown from 'shared/components/common/dropdown'
import { DropDownOption } from 'shared/components/common/dropdown/DropDownOption'
import { toDayString, toLocalTime } from 'shared/js/timeUtils'

import {
  getDateTimeIndexes,
  useFulfillment
} from 'public/components/online_ordering/FulfillmentContext'
import { useTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext'
import { t } from 'src/translations'

const FulfillmentTimeDropdowns = ({
  fulfillmentTime,
  setFulfillmentTime,
  draftFulfillmentType,
  fulfillmentTimeExpired
}: {
  fulfillmentTime: string | null | undefined
  setFulfillmentTime: (time: string | undefined) => void
  draftFulfillmentType: FulfillmentType
  fulfillmentTimeExpired: boolean
}) => {
  const {
    selectedDiningOptionBehaviorSchedules,
    selectedDiningOptionBehavior
  } = useFulfillment()
  const {
    getFilteredFutureSchedules,
    itemInCartWithMaxLeadTime,
    getEarliestFulfillmentTime
  } = useTimeBasedRules()
  const futureSchedules = useMemo(
    () =>
      getFilteredFutureSchedules(
        getEarliestFulfillmentTime(
          itemInCartWithMaxLeadTime?.itemGuid,
          selectedDiningOptionBehavior
        ),
        selectedDiningOptionBehaviorSchedules?.futureScheduleDates ?? [],
        selectedDiningOptionBehavior
      ),
    [
      selectedDiningOptionBehaviorSchedules,
      selectedDiningOptionBehavior,
      getFilteredFutureSchedules,
      getEarliestFulfillmentTime,
      itemInCartWithMaxLeadTime?.itemGuid
    ]
  )
  const [selectedDayIndex, setSelectedDayIndex] = useState(-1)
  const [selectedTimeIndex, setSelectedTimeIndex] = useState(-1)

  // Set the dropdowns to reflect either the selected fulfillment time, or the earliest available scheduled time
  useEffect(() => {
    const dateTimeIndexes = getDateTimeIndexes(
      fulfillmentTime ||
        futureSchedules?.find((date) => date?.times?.length)?.times?.[0]?.time,
      futureSchedules
    )
    if (!dateTimeIndexes) {
      return
    }

    const [dayIndex, timeIndex] = dateTimeIndexes
    const tempDayIndex = Math.max(dayIndex || 0, 0)
    const tempTimeIndex = Math.max(timeIndex || 0, 0)
    setSelectedDayIndex(tempDayIndex)
    setSelectedTimeIndex(tempTimeIndex)
    if (
      fulfillmentTimeExpired ||
      (!fulfillmentTime && draftFulfillmentType === FulfillmentType.Future)
    ) {
      setFulfillmentTime(
        futureSchedules[tempDayIndex]?.times[tempTimeIndex]?.time
      )
    }
  }, [
    fulfillmentTime,
    selectedDiningOptionBehaviorSchedules,
    futureSchedules,
    setFulfillmentTime,
    draftFulfillmentType,
    fulfillmentTimeExpired
  ])

  const normalizedDayIndex = Math.max(0, selectedDayIndex)
  const normalizedTimeIndex = Math.max(0, selectedTimeIndex)
  const day =
    futureSchedules?.length && futureSchedules[normalizedDayIndex]?.times.length
      ? futureSchedules[normalizedDayIndex]
      : futureSchedules?.find((date) => date?.times?.length)
  const time = day?.times?.length ? day.times[normalizedTimeIndex] : null
  const timeOptions = futureSchedules[normalizedDayIndex]?.times || []
  const dayLabel = day ? toDayString(day.date) : 'Today'
  const timeLabel = time ? toLocalTime(time.time) : undefined

  return (
    <div className='fulfillmentTimeDropdowns'>
      <div className='selector'>
        <label id='date-dropdown-label' className='dropdownLabel'>
          {t('fulfillment-time-dropdown.date')}
        </label>
        <DropDown
          label={dayLabel}
          withBorder
          testId='fulfillment-date-selector'
          ariaDescription={t('fulfillment-time-dropdown.date-desc')}
          labelledBy='date-dropdown-label'
        >
          {({ close }) =>
            futureSchedules?.map((date, index) =>
              date.times.length ? (
                <DropDownOption
                  className='option'
                  testId='fulfillment-date-list-option'
                  key={date.date}
                  onSelect={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setSelectedDayIndex(index)
                    setFulfillmentTime(
                      futureSchedules[Math.max(index, 0)]?.times[
                        Math.max(selectedTimeIndex, 0)
                      ]?.time
                    )
                    close()
                  }}
                  onClose={close}
                >
                  {toDayString(date.date)}
                </DropDownOption>
              ) : null
            )
          }
        </DropDown>
      </div>
      <div className='selector'>
        <label className='dropdownLabel' id='time-dropdown-label'>
          {t('fulfillment-time-dropdown.time')}
        </label>
        <DropDown
          label={timeLabel}
          withBorder
          disabled={timeOptions.length === 0}
          testId='fulfillment-time-selector'
          ariaDescription={t('fulfillment-time-dropdown.time-desc')}
          labelledBy='time-dropdown-label'
        >
          {({ close }) =>
            timeOptions.map((time, index) => (
              <DropDownOption
                key={time.time}
                className='option'
                testId='fulfillment-time-list-option'
                onSelect={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setSelectedTimeIndex(index)
                  setFulfillmentTime(
                    futureSchedules[Math.max(selectedDayIndex, 0)]?.times[
                      Math.max(index, 0)
                    ]?.time
                  )
                  close()
                }}
                onClose={close}
              >
                {toLocalTime(time.time)}
              </DropDownOption>
            ))
          }
        </DropDown>
      </div>
    </div>
  )
}

export default FulfillmentTimeDropdowns
